// Human-readable URL slugs.
//
// Documents with a unique serial (requisitions, purchase orders) are addressed by that
// number, lower-cased (e.g. /purchase-orders/po-000123). Named records whose names are not
// guaranteed unique (suppliers, catalog items) use a readable name slug followed by a short
// id suffix, e.g. /suppliers/acme-foods-ltd-016de89b — the trailing token is what we resolve
// against (the API matches the end of the stored id), so the leading text is purely cosmetic
// and older full-id links keep working too.

// Length of the id suffix shown in URLs. 8 hex chars = ~4.3 billion combinations, unique
// enough within a tenant while staying short and readable.
const ID_SUFFIX_LEN = 8;

export function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'item';
}

/** Build a "name-slug + short id" reference for named records. */
export function namedSlug(name, id) {
  if (!id) return '';
  return `${slugify(name)}-${String(id).slice(-ID_SUFFIX_LEN)}`;
}

/**
 * Extract the canonical lookup key from a route param.
 * - Named records: the id token is always the last dash-separated segment (short suffix, or a
 *   full id on older links). The API resolves a short hex suffix by matching the end of ids.
 * - Serial numbers (e.g. "po-000123") aren't run through this helper.
 */
export function refFromSlug(param) {
  if (!param) return param;
  const token = String(param).split('-').pop();
  return token || param;
}

// --- Path builders -----------------------------------------------------------

export const supplierPath = (supplier) =>
  `/suppliers/${namedSlug(supplier?.name, supplier?.id)}`;

export const itemPath = (item) =>
  `/catalog/items/${namedSlug(item?.name, item?.id)}`;

export const requisitionPath = (req, suffix = '') =>
  `/requisitions/${(req?.number || req?.id || '').toLowerCase()}${suffix}`;

export const purchaseOrderPath = (po, suffix = '') =>
  `/purchase-orders/${(po?.number || po?.id || '').toLowerCase()}${suffix}`;

export const goodsReceiptPath = (grn, suffix = '') =>
  `/receiving/${(grn?.number || grn?.id || '').toLowerCase()}${suffix}`;

export const supplierReturnPath = (ret, suffix = '') =>
  `/returns/${(ret?.number || ret?.id || '').toLowerCase()}${suffix}`;

export const stockCountPath = (count, suffix = '') =>
  `/counts/${(count?.number || count?.id || '').toLowerCase()}${suffix}`;

export const stockTransferPath = (transfer, suffix = '') =>
  `/transfers/${(transfer?.number || transfer?.id || '').toLowerCase()}${suffix}`;

export const stockAdjustmentPath = (adjustment, suffix = '') =>
  `/adjustments/${(adjustment?.number || adjustment?.id || '').toLowerCase()}${suffix}`;

export const invoicePath = (invoice, suffix = '') =>
  `/invoices/${(invoice?.number || invoice?.id || '').toLowerCase()}${suffix}`;
